import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, rest } from '@/lib/supabase';
import { queryKeys } from '@/lib/queryClient';
import type { CaseStudy } from '@/types/database';

export function useCaseStudies(options?: { includeUnpublished?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.caseStudies.list(), options],
    queryFn: async () => {
      let query = rest.from<CaseStudy>('case_studies')
        .select('*')
        .order('published_at', { ascending: false });

      if (!options?.includeUnpublished) {
        query = query.eq('status', 'published');
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CaseStudy[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCaseStudy(slug: string) {
  return useQuery({
    queryKey: queryKeys.caseStudies.detail(slug),
    queryFn: async () => {
      const { data, error } = await rest.from<CaseStudy>('case_studies')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as CaseStudy;
    },
    enabled: !!slug,
  });
}

export function useFeaturedCaseStudies() {
  return useQuery({
    queryKey: [...queryKeys.caseStudies.all, 'featured'],
    queryFn: async () => {
      const { data, error } = await rest.from<CaseStudy>('case_studies')
        .select('*')
        .eq('status', 'published')
        .eq('is_featured', true)
        .order('order_index', { ascending: true })
        .limit(3);

      if (error) throw error;
      return data as CaseStudy[];
    },
  });
}

export function useCreateCaseStudy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (caseStudy: Omit<CaseStudy, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('case_studies')
        .insert(caseStudy as any)
        .select()
        .single();

      if (error) throw error;
      return data as CaseStudy;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseStudies.all });
    },
  });
}

export function useUpdateCaseStudy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CaseStudy> & { id: string }) => {
      const { data, error } = await supabase
        .from('case_studies')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as CaseStudy;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseStudies.all });
      if (data?.slug) {
        queryClient.invalidateQueries({ queryKey: queryKeys.caseStudies.detail(data.slug) });
      }
    },
  });
}

export function useDeleteCaseStudy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('case_studies')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseStudies.all });
    },
  });
}
